import { AdminCalRec } from "../layouts/AdminCalRec";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { useState, useEffect } from "react";
import "../assets/css/dashboard.css";
import { useLocation } from "react-router-dom";
import CustomModal from "../components/CustomModal/CustomModal";
import { GrowthMonitor } from "./GrowthMonitor";
import { defaultUrl, fetchConversationsThunk } from "../features/actions";
import DashboardPhotoCard from "../components/DashboardPhotoCard";
import { useSelector } from "react-redux";
import { getMediaThunk } from "../features/mediaSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { makeFetchRequest } from "../utilities/common";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// ─── Token ────────────────────────────────────────────────────────────────────
let token = "";
const stringifiedToken = sessionStorage.getItem("token");
if (typeof stringifiedToken === "string") token = stringifiedToken;
export const globalToken = token;

// ─── Stat Card ────────────────────────────────────────────────────────────────
type StatCardProps = {
  label: string;
  value: number | string;
  icon: string;
  accent: "blue" | "teal" | "orange";
  badge?: string;
  badgeUp?: boolean;
};

const ACCENT_MAP = {
  blue: {
    bar: "bg-[#0096D1]",
    iconBg: "bg-[#E6F4FB]",
    iconText: "text-[#0096D1]",
  },
  teal: {
    bar: "bg-[#00C6A2]",
    iconBg: "bg-[#E0FAF5]",
    iconText: "text-[#00C6A2]",
  },
  orange: {
    bar: "bg-[#FF6B35]",
    iconBg: "bg-[#FFF0EB]",
    iconText: "text-[#FF6B35]",
  },
};

const StatCard = ({
  label,
  value,
  icon,
  accent,
  badge,
  badgeUp,
}: StatCardProps) => {
  const a = ACCENT_MAP[accent];
  return (
    <div className="relative bg-white rounded-2xl border border-[#012063]/10 p-5 overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 cursor-default">
      <div
        className={`absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl ${a.bar}`}
      />
      <div
        className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg mb-3 ${a.iconBg} ${a.iconText}`}
      >
        {icon}
      </div>
      <p className="text-[11px] font-medium text-[#5A7BA6] uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="font-extrabold text-[32px] leading-none text-[#012063] tracking-tight">
        {value}
      </p>
      {badge && (
        <p className="mt-1 text-[11px] flex items-center gap-1">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
              badgeUp
                ? "bg-[#E0FAF5] text-[#0F6E56]"
                : "bg-[#FAECEA] text-[#A32D2D]"
            }`}
          >
            {badge}
          </span>
        </p>
      )}
    </div>
  );
};

// ─── Activity Row ──────────────────────────────────────────────────────────────
type ActRowProps = {
  icon: string;
  iconBg: string;
  iconColor: string;
  label: string;
  value: number;
};
const ActRow = ({ icon, iconBg, iconColor, label, value }: ActRowProps) => (
  <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-[#F4F7FB] hover:bg-[#EAF5FB] transition-colors">
    <div className="flex items-center gap-2.5">
      <div
        className={`w-[30px] h-[30px] rounded-lg flex items-center justify-center text-base flex-shrink-0`}
        style={{ background: iconBg, color: iconColor }}
      >
        {icon}
      </div>
      <span className="text-[13px] font-medium text-[#012063]">{label}</span>
    </div>
    <span className="font-extrabold text-lg text-[#012063] tracking-tight">
      {value}
    </span>
  </div>
);

// ─── Quick Stat Bar ─────────────────────────────────────────────────────────────
const QuickStat = ({
  label,
  value,
  max,
  color,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
}) => (
  <div className="flex items-center justify-between text-xs gap-2">
    <span className="text-[#5A7BA6] w-24 shrink-0">{label}</span>
    <div className="flex-1 h-1 rounded-full bg-[#012063]/[0.07]">
      <div
        className="h-full rounded-full"
        style={{
          width: `${Math.round((value / max) * 100)}%`,
          background: color,
        }}
      />
    </div>
    <span className="font-semibold text-[#012063] w-6 text-right">{value}</span>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
export const AdminHome = () => {
  const location = useLocation();
  const token = location.state?.token;
  const dispatch = useAppDispatch();

  const { images: photos } =
    useSelector((state: any): any => state.photos) || [];
  const { conversations } =
    useAppSelector((state: any): any => state.messages) || [];

  const [showGrowthMonitor, setShowGrowthMonitor] = useState(false);
  const [summaryData, setSummaryData] = useState({
    absent: 0,
    birthdays: 0,
    students: 0,
    classrooms: 0,
    loggedin: 0,
    loggedout: 0,
    messages: 0,
    "non-teachers": 0,
    parent: 0,
    photosandvideos: 0,
    teachers: 0,
  });

  useEffect(() => {
    fetchDashboardData();
    dispatch(fetchConversationsThunk());
    dispatch(getMediaThunk());
  }, []);

  const fetchDashboardData = async () => {
    const data = await makeFetchRequest(
      `${defaultUrl}/user/dashboard`,
      false,
      false,
      token,
    );
    if (data) setSummaryData(data);
  };
  console.log(summaryData);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const attendanceChartData = {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    datasets: [
      {
        label: "Logged In",
        data: [185, 190, 178, 196, summaryData.loggedin],
        backgroundColor: "#0096D1",
        borderRadius: 6,
        barPercentage: 0.65,
      },
      {
        label: "Logged Out",
        data: [30, 25, 35, 28, summaryData.loggedout],
        backgroundColor: "#00C6A2",
        borderRadius: 6,
        barPercentage: 0.65,
      },
      {
        label: "Absent",
        data: [33, 33, 35, 24, summaryData.absent],
        backgroundColor: "#FF6B35",
        borderRadius: 6,
        barPercentage: 0.65,
      },
    ],
  };

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: "#5A7BA6" },
      },
      y: {
        grid: { color: "rgba(1,32,99,0.05)" },
        ticks: { font: { size: 10 }, color: "#5A7BA6" },
      },
    },
  };

  return (
    <section className="flex-1 h-[98vh] overflow-auto bg-[#F4F7FB] font-['DM_Sans',sans-serif]">
      {/* ── Top Nav ── */}
      <header className="sticky top-0 z-10 bg-white border-b border-[#012063]/10 h-16 flex items-center justify-between px-8">
        <div className="font-extrabold text-[18px] text-[#012063] tracking-tight font-['Syne',sans-serif]">
          Edu<span className="text-[#0096D1]">Admin</span>
        </div>
        <TopAdminNav name="Jane Doe" title="" />
      </header>

      {/* ── Body ── */}
      <div className="flex h-[calc(98vh-64px)]">
        <div className="flex-1 overflow-auto p-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-['Syne',sans-serif] font-extrabold text-3xl text-[#012063] tracking-tight leading-none">
              Dashboard
            </h1>
            <p className="text-sm text-[#5A7BA6] mt-1">
              Here's what's happening at your school today
            </p>
            <span className="mt-2 inline-flex items-center gap-1.5 bg-[#0096D1] text-white rounded-full px-3 py-1 text-[11px] font-medium">
              📅 {today}
            </span>
          </div>

          {/* Metric Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard
              label="Active Children"
              value={summaryData.students}
              icon="👦"
              accent="blue"
              badge="↑ 4.2% from last term"
              badgeUp
            />
            <StatCard
              label="Checked In Today"
              value={summaryData.loggedin}
              icon="✅"
              accent="teal"
              badge="78% attendance rate"
              badgeUp
            />
            <StatCard
              label="Classrooms"
              value={summaryData.classrooms}
              icon="🏫"
              accent="orange"
              badge="All active"
              badgeUp
            />
          </div>

          {/* Attendance + Activities Row */}
          <div className="grid grid-cols-[1.4fr_1fr] gap-4 mb-6">
            {/* Attendance */}
            <div className="bg-white rounded-2xl border border-[#012063]/10 p-6">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="font-['Syne',sans-serif] font-bold text-[15px] text-[#012063]">
                    Attendance Report
                  </p>
                  <p className="text-xs text-[#5A7BA6] mt-0.5">
                    Weekly check-in overview
                  </p>
                </div>
                <button
                  onClick={() => (window.location.href = "/admin/attendance")}
                  className="text-[11px] text-[#0096D1] bg-[#0096D1]/10 hover:bg-[#0096D1]/20 border-none rounded-full px-3 py-1 font-medium cursor-pointer transition-colors"
                >
                  View report →
                </button>
              </div>
              <div className="h-[160px] w-full">
                <Bar data={attendanceChartData} options={chartOptions} />
              </div>
              <div className="flex gap-4 mt-4 flex-wrap">
                {[
                  {
                    dot: "#0096D1",
                    label: "Logged In",
                    val: summaryData.loggedin,
                  },
                  {
                    dot: "#00C6A2",
                    label: "Logged Out",
                    val: summaryData.loggedout,
                  },
                  {
                    dot: "#FF6B35",
                    label: "Absent",
                    val: summaryData.absent,
                  },
                ].map((l) => (
                  <div
                    key={l.label}
                    className="flex items-center gap-1.5 text-xs text-[#5A7BA6]"
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: l.dot }}
                    />
                    {l.label}:{" "}
                    <strong className="text-[#012063] ml-0.5">{l.val}</strong>
                  </div>
                ))}
              </div>
            </div>

            {/* Activities */}
            <div className="bg-white rounded-2xl border border-[#012063]/10 p-6">
              <div className="mb-5">
                <p className="font-['Syne',sans-serif] font-bold text-[15px] text-[#012063]">
                  Activities
                </p>
                <p className="text-xs text-[#5A7BA6] mt-0.5">
                  Recent school activity
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <ActRow
                  icon="💬"
                  iconBg="#E6F4FB"
                  iconColor="#0096D1"
                  label="Messages"
                  value={summaryData.messages}
                />
                <ActRow
                  icon="🎂"
                  iconBg="#FFF0EB"
                  iconColor="#FF6B35"
                  label="Birthdays"
                  value={summaryData.birthdays}
                />
                <ActRow
                  icon="📷"
                  iconBg="#E0FAF5"
                  iconColor="#00C6A2"
                  label="Photos & Videos"
                  value={summaryData.photosandvideos}
                />
                <ActRow
                  icon="👨‍👩‍👧"
                  iconBg="#F4EFF9"
                  iconColor="#7B2FBE"
                  label="Parents"
                  value={summaryData.parent}
                />
              </div>
            </div>
          </div>

          {/* Growth Monitor CTA */}
          <div className="bg-white rounded-2xl border border-[#012063]/10 p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="font-['Syne',sans-serif] font-bold text-[15px] text-[#012063]">
                  Growth Monitor
                </p>
                <p className="text-xs text-[#5A7BA6] mt-0.5">
                  Enrolment & revenue trends across years
                </p>
              </div>
              <button
                onClick={() => setShowGrowthMonitor(true)}
                className="text-[11px] text-[#0096D1] bg-[#0096D1]/10 hover:bg-[#0096D1]/20 border-none rounded-full px-3 py-1 font-medium cursor-pointer transition-colors"
              >
                Open full view →
              </button>
            </div>
            {/* Mini preview */}
            <div className="flex gap-6">
              {[
                {
                  label: "Avg Enrolment Growth",
                  value: "+18%",
                  color: "#0096D1",
                },
                { label: "Peak Year", value: "2022", color: "#00C6A2" },
                { label: "Revenue Growth", value: "+400%", color: "#FF6B35" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="flex-1 bg-[#F4F7FB] rounded-xl p-4"
                >
                  <p className="text-[10px] text-[#5A7BA6] font-medium uppercase tracking-wide mb-1">
                    {s.label}
                  </p>
                  <p
                    className="font-['Syne',sans-serif] font-extrabold text-2xl text-[#012063]"
                    style={{ color: s.color }}
                  >
                    {s.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Recently Added */}
          <div className="bg-white rounded-2xl border border-[#012063]/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-['Syne',sans-serif] font-bold text-[15px] text-[#012063]">
                  Recently Added
                </p>
                <p className="text-xs text-[#5A7BA6] mt-0.5">
                  Photos & videos from classrooms
                </p>
              </div>
              <button className="text-[11px] text-[#0096D1] bg-[#0096D1]/10 hover:bg-[#0096D1]/20 border-none rounded-full px-3 py-1 font-medium cursor-pointer transition-colors">
                View all →
              </button>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {photos && photos.length > 0
                ? photos.slice(0, 10).map((item: any, index: number) => (
                    <div key={index} className="aspect-square">
                      <DashboardPhotoCard item={item} />
                    </div>
                  ))
                : Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-xl flex items-center justify-center text-2xl text-[#5A7BA6]"
                      style={{
                        background: [
                          "#D4ECF7",
                          "#D4F7EC",
                          "#F7E6D4",
                          "#EBD4F7",
                          "#F7D4D4",
                        ][i],
                      }}
                    >
                      {["🖼️", "🎥", "🖼️", "🖼️", "🎥"][i]}
                    </div>
                  ))}
            </div>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <aside className="hidden 2xl:flex flex-col gap-4 w-72 border-l border-[#012063]/10 bg-white p-5 overflow-auto">
          <div className="bg-[#F4F7FB] rounded-xl p-4">
            <AdminCalRec />
          </div>

          {/* Upcoming Events */}
          <div className="bg-[#F4F7FB] rounded-xl p-4">
            <p className="font-['Syne',sans-serif] font-bold text-[13px] text-[#012063] mb-3 flex items-center gap-1.5">
              🕐 Upcoming
            </p>
            <div className="flex flex-col gap-3">
              {[
                {
                  color: "#FF6B35",
                  title: "End-of-term parent meeting",
                  date: "May 28, 2026",
                },
                {
                  color: "#0096D1",
                  title: "School sports day",
                  date: "May 30, 2026",
                },
                {
                  color: "#00C6A2",
                  title: "New term enrolment opens",
                  date: "June 4, 2026",
                },
              ].map((e) => (
                <div key={e.title} className="flex gap-2.5 items-start">
                  <div
                    className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                    style={{ background: e.color }}
                  />
                  <div>
                    <p className="text-xs text-[#012063] leading-snug">
                      {e.title}
                    </p>
                    <p className="text-[10px] text-[#5A7BA6] mt-0.5">
                      {e.date}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-[#F4F7FB] rounded-xl p-4">
            <p className="font-['Syne',sans-serif] font-bold text-[13px] text-[#012063] mb-3 flex items-center gap-1.5">
              📊 Quick Stats
            </p>
            <div className="flex flex-col gap-2.5">
              <QuickStat
                label="Teachers"
                value={summaryData.teachers}
                max={30}
                color="#0096D1"
              />
              <QuickStat
                label="Non-teaching"
                value={summaryData["non-teachers"]}
                max={30}
                color="#00C6A2"
              />
              <QuickStat
                label="Parents"
                value={summaryData.parent}
                max={100}
                color="#FF6B35"
              />
              <QuickStat
                label="Classrooms"
                value={summaryData.classrooms}
                max={25}
                color="#7B2FBE"
              />
            </div>
          </div>
        </aside>
      </div>

      {/* Growth Monitor Modal */}
      {showGrowthMonitor && (
        <CustomModal
          width={90}
          title="Growth Monitor"
          children={<GrowthMonitor />}
          hideModal={() => setShowGrowthMonitor(false)}
        />
      )}
    </section>
  );
};
